import { gql } from '@apollo/client'
import { BodyText, Heading, Modal, ModalProps, VisuallyHidden } from '@farol-ds/react'
import { ReactNode } from 'react'

import { useSendViewItemEvent } from '../../../../hooks/use-send-view-item-event'
import { useModalContext } from '../../../context'
import { LockIcon } from '../../../lock-icon'
import { ModalLock_OfferLockDataFragment } from './__generated__/modal-lock.graphql'
import styles from './modal-lock.module.scss'

export interface ModalLockProps {
  children: ReactNode
  size?: ModalProps['size']
  headingSize?: 'sm' | 'md' | 'lg'
  modalContentClassName?: string
  offerLockData: ModalLock_OfferLockDataFragment
}

export function ModalLock({
  children,
  offerLockData,
  size = 'lg',
  headingSize = 'md',
  modalContentClassName,
}: ModalLockProps) {
  const { header, body } = offerLockData
  const title = header?.title
  const subtitle = header?.subtitle
  const { open, setOpen } = useModalContext()
  const { sendViewItemRef } = useSendViewItemEvent({ offerLockBody: body })

  return (
    <Modal size={size} open={open} onOpenChange={setOpen}>
      <Modal.Content data-testid="modal-lock-content" containerClassName={modalContentClassName}>
        <Modal.IconClose data-testid="modal-lock-icon-close" />

        <Modal.Header data-testid="modal-lock-header">
          <VisuallyHidden>Jusbrasil</VisuallyHidden>

          <div className={styles.modalLockHeader}>
            {header?.icon && (
              <LockIcon icon={header?.icon} className={styles.modalLockHeaderIcon} />
            )}

            <div className={styles.modalLockHeaderContent}>
              {title && (
                <Modal.HeaderTitle asChild>
                  <Heading size={headingSize} className={styles.modalLockHeaderText}>
                    {title}
                  </Heading>
                </Modal.HeaderTitle>
              )}
              {subtitle && (
                <Modal.HeaderDescription asChild>
                  <BodyText size="md" className={styles.modalLockHeaderText} asChild>
                    <span dangerouslySetInnerHTML={{ __html: subtitle }} />
                  </BodyText>
                </Modal.HeaderDescription>
              )}
            </div>
          </div>
        </Modal.Header>

        <VisuallyHidden>
          <h1>Assine o Jusbrasil</h1>
        </VisuallyHidden>

        <Modal.Body data-testid="modal-lock-body" ref={sendViewItemRef}>
          {children}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  )
}

ModalLock.fragments = {
  offerLockData: gql`
    fragment ModalLock_offerLockData on OfferLockData {
      header {
        title
        subtitle
        icon {
          ...LockIcon_offerLockIcon
        }
      }
      body {
        ...UseSendViewItemEvent_offerLockBody
      }
    }

    ${useSendViewItemEvent.fragments.offerLockBody}
    ${LockIcon.fragments.icon}
  `,
}
